import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
export const GhostSuggestions = Extension.create({
    name: 'ghostSuggestions',
    addStorage() {
        return {
            pluginKey: null,
        };
    },
    addOptions() {
        return {
            triggerCharacter: '/',
            getSuggestion: async (query) => {
                // Simple mock suggestions
                await new Promise(resolve => setTimeout(resolve, 300));
                const suggestions = [
                    'Create a new customer onboarding checklist',
                    'Write a bug report template',
                    'Document the deployment process',
                    'Outline employee training procedures',
                    'Draft a meeting agenda template',
                ];
                return suggestions.find(s => s.toLowerCase().includes(query.toLowerCase())) || `Continue writing about "${query}"...`;
            },
        };
    },
    addCommands() {
        return {
            acceptSuggestion: () => ({ tr, state, dispatch }) => {
                try {
                    const pluginState = this.storage.pluginKey?.getState(state);
                    if (!pluginState?.suggestion || !dispatch) {
                        return false;
                    }
                    const { from, to, suggestion } = pluginState;
                    // Clear the suggestion first
                    tr.setMeta(this.storage.pluginKey, { type: 'clear' });
                    // Then insert the text
                    tr.insertText(suggestion, from, to);
                    dispatch(tr);
                    return true;
                }
                catch (error) {
                    console.error('Error accepting suggestion:', error);
                    return false;
                }
            },
            dismissSuggestion: () => ({ tr, state: _state, dispatch }) => {
                try {
                    tr.setMeta(this.storage.pluginKey, { type: 'clear' });
                    if (dispatch)
                        dispatch(tr);
                    return true;
                }
                catch (error) {
                    console.error('Error dismissing suggestion:', error);
                    return false;
                }
            },
        };
    },
    addKeyboardShortcuts() {
        return {
            Tab: () => {
                try {
                    const pluginState = this.storage.pluginKey?.getState(this.editor.state);
                    if (pluginState?.suggestion) {
                        return this.editor.commands.acceptSuggestion();
                    }
                }
                catch (error) {
                    console.error('Error handling Tab:', error);
                }
                return false;
            },
            Escape: () => {
                try {
                    const pluginState = this.storage.pluginKey?.getState(this.editor.state);
                    if (pluginState?.suggestion) {
                        return this.editor.commands.dismissSuggestion();
                    }
                }
                catch (error) {
                    console.error('Error handling Escape:', error);
                }
                return false;
            },
        };
    },
    addProseMirrorPlugins() {
        const pluginKey = new PluginKey('ghostSuggestions');
        this.storage.pluginKey = pluginKey;
        const extension = this;
        return [
            new Plugin({
                key: pluginKey,
                state: {
                    init() {
                        return {
                            suggestion: null,
                            query: '',
                            from: 0,
                            to: 0,
                        };
                    },
                    apply(tr, oldState) {
                        try {
                            const meta = tr.getMeta(pluginKey);
                            if (meta?.type === 'clear') {
                                return {
                                    suggestion: null,
                                    query: '',
                                    from: 0,
                                    to: 0,
                                };
                            }
                            if (meta?.type === 'set') {
                                return {
                                    ...oldState,
                                    ...meta.data,
                                };
                            }
                            // Check if document changed
                            if (tr.docChanged && tr.selection.empty) {
                                const { $from } = tr.selection;
                                const textInNode = $from.parent.textContent || '';
                                const cursorPos = $from.parentOffset;
                                // Find the trigger character
                                const triggerIndex = textInNode.lastIndexOf(extension.options.triggerCharacter, cursorPos - 1);
                                if (triggerIndex >= 0 && triggerIndex < cursorPos) {
                                    const query = textInNode.slice(triggerIndex + 1, cursorPos);
                                    // Trigger for any query (removed space restriction)
                                    if (query.length >= 0) {
                                        const from = $from.start() + triggerIndex;
                                        const to = $from.start() + cursorPos;
                                        // Debounced suggestion fetch
                                        setTimeout(() => {
                                            extension.options.getSuggestion(query)
                                                .then(suggestion => {
                                                if (suggestion && suggestion.length > query.length && extension.editor?.view) {
                                                    const currentState = extension.editor.view.state;
                                                    const currentPluginState = pluginKey.getState(currentState);
                                                    // Only update if we're still in the same position
                                                    if (currentPluginState?.from === from && currentPluginState?.to === to) {
                                                        extension.editor.view.dispatch(currentState.tr.setMeta(pluginKey, {
                                                            type: 'set',
                                                            data: { suggestion, query, from, to },
                                                        }));
                                                    }
                                                }
                                            })
                                                .catch(error => console.error('Error fetching suggestion:', error));
                                        }, 300);
                                        return { ...oldState, query, from, to };
                                    }
                                }
                                // Clear suggestion if cursor moved
                                if (oldState.suggestion) {
                                    return { suggestion: null, query: '', from: 0, to: 0 };
                                }
                            }
                            return oldState;
                        }
                        catch (error) {
                            console.error('Error in plugin apply:', error);
                            return oldState;
                        }
                    },
                },
                props: {
                    decorations(state) {
                        try {
                            const pluginState = pluginKey.getState(state);
                            if (!pluginState?.suggestion) {
                                return DecorationSet.empty;
                            }
                            const { to, suggestion, query } = pluginState;
                            if (!suggestion || suggestion.length <= query.length) {
                                return DecorationSet.empty;
                            }
                            return DecorationSet.create(state.doc, [
                                Decoration.widget(to, () => {
                                    const span = document.createElement('span');
                                    span.style.cssText = 'color: #9ca3af; font-style: italic; pointer-events: none; user-select: none;';
                                    span.textContent = suggestion.slice(query.length);
                                    return span;
                                }),
                            ]);
                        }
                        catch (error) {
                            console.error('Error creating decorations:', error);
                            return DecorationSet.empty;
                        }
                    },
                },
            }),
        ];
    },
});
//# sourceMappingURL=GhostSuggestions.js.map